import { Stack, Textarea, TextInput, Title } from '@mantine/core';
import SelectField from './SelectField';
import inputFieldStyles from '../sharedStyles/InputField.module.css';
import styles from './MatterDetailsSection.module.css';
import { matterStatusSelectOptions, usStates } from '@/optionSets';
import { MantineForm } from '../AddEditMatterForm';
import ControlledSelect from './ControlledSelect';
import PracticeAreaSelect from './PracticeAreaSelect';
import { FirmDocument, MatterType } from '@shared/types';
import { useUserSelectOptions } from '@/components/hooks';
import { ChevronsUpDownIcon } from 'lucide-react';
import FeeTypeFields from './FeeTypeFields';
import { SelectOption } from '@/components/types';
import { LoadingView } from '@/components/LoadingView';
import FeeTypeSegmentedControl from './FeeTypeSegmentedControl';
import { getInputPropsSafe } from '@/utils/getInputPropsSafe';
import { assertUnreachable } from '@/utils/assertUnreachable';
import { FeeType } from '../types';
import { v4 as uuidv4 } from 'uuid';
import { constructMatterNameFromFormData } from '../constructMatterNameFromFormData';

const MatterDetailsSection = ({
  mantineForm,
  firm,
  leadSourceOptions,
  practiceAreaOptions,
}: {
  mantineForm: MantineForm;
  firm: FirmDocument | undefined;
  leadSourceOptions: SelectOption[];
  practiceAreaOptions: SelectOption[];
}) => {
  const feeTypes: FeeType[] = [
    'Insurance',
    'Hourly',
    'FlatFee',
    'Contingency',
    'Unknown',
  ];
  const userSelectOptions = useUserSelectOptions();

  const subStatusOptions = firm?.subStatuses
    ?.filter((subStatus) => {
      if (subStatus.deletedTimestamp) {
        return false;
      }
      return mantineForm.getValues().matterStatus === subStatus.parentStatus;
    })
    .map((subStatus) => ({
      label: subStatus.name,
      value: subStatus.id,
    }));

  const handleFeeTypeClick = (type: FeeType) => {
    switch (type) {
      case 'Insurance':
        mantineForm.setValues({
          feeType: type,
          insuranceProvider: 'arag',
          insuranceCases: [
            {
              tempId: uuidv4(),
              firestoreId: null,
              caseId: null,
              memberId: null,
              caseValue: null,
              caseType: null,
              otherInsuranceProviderName: null,
            },
          ],
        });
        break;
      case 'Hourly':
        mantineForm.setValues({
          feeType: 'Hourly',
          retainer: '',
          responsibleAttyHourlyRate: '',
          referralOriginatorId: null,
          engagementOriginatorId: null,
        });
        break;
      case 'FlatFee':
        mantineForm.setValues({
          feeType: 'FlatFee',
          flatFeeAmt: '',
          referralOriginatorId: null,
          engagementOriginatorId: null,
        });
        break;
      case 'Contingency':
        mantineForm.setValues({
          feeType: 'Contingency',
          contingencyFees: {
            pctPreFilingOfLawsuit: null,
            pctUponFilingOfLawsuit: null,
            pctUponCommencementOfTrial: null,
          },
        });
        break;
      case `Unknown`:
        mantineForm.setValues({
          feeType: 'Unknown',
        });
        break;
      default:
        return assertUnreachable(type);
    }
  };

  return (
    <section title='Matter Details' className={styles.matterDetails}>
      <Title order={2} size='h3'>
        Matter Details
      </Title>
      {userSelectOptions.length === 0 ? (
        <LoadingView />
      ) : (
        <div className={styles.matterDetailsFieldsContainer}>
          <TextInput
            className={inputFieldStyles.inputField}
            label='Matter Name'
            key={mantineForm.key('matterName')}
            autoComplete='off'
            placeholder={constructMatterNameFromFormData(
              mantineForm.getValues(),
            )}
            {...getInputPropsSafe(mantineForm, 'matterName')}
          />
          <div className={inputFieldStyles.inputRow}>
            <SelectField
              fieldName='state'
              label='State'
              options={usStates}
              form={mantineForm}
            />
            <SelectField
              fieldName='matterStatus'
              label='Status'
              options={matterStatusSelectOptions}
              form={mantineForm}
            />
          </div>
          {(subStatusOptions ?? []).length > 0 && (
            <div className={inputFieldStyles.inputRow}>
              <SelectField
                fieldName='matterSubStatusId'
                label='Sub Status'
                options={subStatusOptions ?? []}
                form={mantineForm}
              />
            </div>
          )}
          <div className={inputFieldStyles.inputRow}>
            <ControlledSelect
              fieldName='responsibleAttorneyId'
              label='Responsible Attorney / LLP'
              placeholder='Select responsible attorney or LLP'
              options={userSelectOptions}
              form={mantineForm}
              clearable={false}
            />
            <ControlledSelect
              fieldName='otherAssigneeId'
              label='Other Assignee'
              placeholder='Select other assignee'
              options={userSelectOptions}
              form={mantineForm}
              clearable={true}
            />
          </div>
          <Textarea
            label='Description'
            key={mantineForm.key('description')}
            resize='vertical'
            rows={3}
            {...getInputPropsSafe(mantineForm, 'description')}
          />
          <TextInput
            className={inputFieldStyles.inputField}
            label='Scope (for engagement letter)'
            key={mantineForm.key('scope')}
            autoComplete='off'
            {...getInputPropsSafe(mantineForm, 'scope')}
          />
          <div className={inputFieldStyles.inputRow}>
            <PracticeAreaSelect
              form={mantineForm}
              practiceAreaOptions={practiceAreaOptions}
            />
            <SelectField
              fieldName='leadSourceId'
              label='Lead Source'
              options={leadSourceOptions}
              form={mantineForm}
            />
          </div>
          <section className={styles.matterType}>
            <Stack gap='xs'>
              <Title order={3}>Select Fee Type</Title>

              <FeeTypeSegmentedControl
                feeTypes={feeTypes}
                mantineForm={mantineForm}
                onChange={handleFeeTypeClick}
              />
            </Stack>

            <FeeTypeFields
              className={styles.matterTypeSpecificFields}
              feeType={mantineForm.getValues().feeType ?? undefined}
              attorneys={userSelectOptions}
              form={mantineForm}
            />
          </section>
        </div>
      )}
    </section>
  );
};

export default MatterDetailsSection;
